/*
  The home Population Report card (Robert, 2026-10-20): the figures that used to run as a
  full-width band under the cert lookup, gathered into one card. Laid out like the events
  storefront's live stats panel (pcg-new-theme snippets/pcg-live-stats.liquid), standing
  beside the banner image as it does there (Robert, 2026-10-20: "make the top the same
  layout as the events"): the total, the "Just certified" ticker, "Graded in October", then
  the four top grades two by two, each with its share of all cards graded and a gold bar.

  **The figures and their rules are still `statsBandState`'s.** Its payload guard is why a
  renamed /counts field hides the card instead of printing "0 cards graded", and the
  labels ("Flawless 10", not "Quad 10") live there too. This file only lays them out.

  Three states, so the banner image beside it never jumps:
    - loading: the card's frame with blank figures, the same size as the loaded card;
    - ready: the figures;
    - error or a bad payload: nothing, and HomeShowcase lets the banner image take the row
      (it reads the same answer through usePopulationStats).

  "Live" is earned rather than decorative: /counts is re-read every minute while the home
  page is open, and the ticker's feed every two.

  Brought level with the storefront panel's later additions (Robert, 2026-10-20: "get the
  new update on the stats from the event page"):
    - "Graded in October": /counts `monthGraded`, hidden when the backend does not send it;
    - the ticker reads the most recently COMPLETED cards, and shows each one's game, slab
      and date. It used to name the grading office too ("Graded by PCG Japan"); Robert had
      that removed on 2026-10-22 ("remove region here");
    - the figures count up when they land and when a poll changes them (not under reduced
      motion). The storefront's "Explore the Population Report" button is left out: this
      is the Population Report.

  Nothing here is focusable except the ticker's link to the cert's report. It sits outside
  SearchCardsRow's panel, so focusing it closes an open results overlay, which is right.
*/

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { CONTROL_LABEL, FOCUS_RING } from 'components/styles';
import {
  formatCount,
  formatShare,
  gradeShare,
  monthGradedLabel,
  selectJustCertified,
  selectMonthGraded,
  statsBandState,
} from 'lib';
import type { StatsBandState } from 'lib';

import { useGetCountsQuery, useGetRecentGradingsQuery } from 'services/pcgPublic';
import { useGetGamesQuery } from 'services/pcgPublicV2';

const COUNTS_POLL_MS = 60_000;
const RECENT_POLL_MS = 120_000;
const ROTATE_MS = 4_500;
const FADE_MS = 300;

/* The four grade tiles, in the order the storefront shows them. Keys are statsBandState's. */
const GRADE_KEYS = ['quad10', 'pristine10', 'gemMint10', 'gemMint95'] as const;

/* Inner boxes: the ticker and the tiles. A shade off the card, with a soft edge. */
const INNER = 'rounded-xl border border-pcg-borderSoft bg-pcg-raised/40';

const BLANK = ' ';

/*
  "13 PCG offices worldwide" beside games tracked (Robert, 2026-10-21). A fixed number on
  purpose: no public endpoint lists the offices (/sites is the 11 Shopify stores, Events and
  the EU router among them), so this is the count Robert confirmed, the 13 countries on the
  home banner: New Zealand, Australia, Malaysia, Philippines, Thailand, Japan, Germany,
  England, USA, Chile, Norway, Brazil, Canada. Change it when an office opens or closes.
*/
const PCG_OFFICE_COUNT = 13;

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) {
      return undefined;
    }
    const onChange = () => setReduced(query.matches);
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
};

/*
  A figure that counts up to its value: cubic ease-out, 1.6s from zero, 0.9s from a previous
  value when a poll changes it, the storefront's timings. Reduced motion shows the value at
  once. Screen readers get the final value (aria-label), never the rolling digits.
*/
const useCountUp = (target: number, reducedMotion: boolean): number => {
  const [shown, setShown] = useState(reducedMotion ? target : 0);
  const shownRef = useRef(shown);
  shownRef.current = shown;

  useEffect(() => {
    const from = shownRef.current;
    if (reducedMotion || from === target) {
      setShown(target);
      return undefined;
    }
    const span = from ? 900 : 1600;
    let frame = 0;
    let start: number | null = null;
    const step = (time: number) => {
      if (start === null) {
        start = time;
      }
      const progress = Math.min(1, (time - start) / span);
      const eased = 1 - Math.pow(1 - progress, 3);
      setShown(Math.round(from + (target - from) * eased));
      if (progress < 1) {
        frame = window.requestAnimationFrame(step);
      }
    };
    frame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(frame);
  }, [target, reducedMotion]);

  return shown;
};

const CountUp = ({ value, className }: { value: number; className?: string }) => {
  const reducedMotion = usePrefersReducedMotion();
  const shown = useCountUp(value, reducedMotion);
  return (
    <span className={className} aria-label={formatCount(value)}>
      <span aria-hidden="true">{formatCount(shown)}</span>
    </span>
  );
};

const JustCertifiedTicker = () => {
  const recent = useGetRecentGradingsQuery(undefined, { pollingInterval: RECENT_POLL_MS });
  const rows = selectJustCertified(recent.data?.data);
  const reducedMotion = usePrefersReducedMotion();

  const [at, setAt] = useState(0);
  const [fading, setFading] = useState(false);

  /* A fresh feed starts from its newest row. */
  useEffect(() => {
    setAt(0);
  }, [recent.data]);

  useEffect(() => {
    if (rows.length < 2) {
      return undefined;
    }
    let fadeTimer: number | undefined;
    const timer = window.setInterval(() => {
      if (document.hidden) {
        return;
      }
      if (reducedMotion) {
        setAt(current => current + 1);
        return;
      }
      setFading(true);
      fadeTimer = window.setTimeout(() => {
        setAt(current => current + 1);
        setFading(false);
      }, FADE_MS);
    }, ROTATE_MS);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(fadeTimer);
    };
  }, [rows.length, reducedMotion]);

  /* No feed, or nothing certified in it: no box, the way the storefront hides it. */
  if (rows.length === 0) {
    return null;
  }

  const row = rows[at % rows.length];

  return (
    <div className={`${INNER} min-w-0 px-4 py-3`}>
      <p className={CONTROL_LABEL}>Just certified</p>
      <Link
        to={`/report/${row.gradedId}`}
        title={[row.gradeLabel, row.cardName, row.gameName, `Cert #${row.gradedId}`].filter(Boolean).join(' · ')}
        className={`mt-1 block min-w-0 rounded-sm transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none ${
          fading ? 'translate-y-1.5 opacity-0' : 'translate-y-0 opacity-100'
        } ${FOCUS_RING}`}
      >
        <span className="block truncate text-sm text-pcg-text">
          <b className="font-bold text-pcg-goldText">{row.gradeLabel}</b>
          {row.cardName ? ` · ${row.cardName}` : ''}
        </span>
        {row.gameName ? <span className="block truncate text-xs text-pcg-text/80">{row.gameName}</span> : null}
        <span className="block truncate text-xs text-pcg-textMuted">
          {[`Cert #${row.gradedId}`, row.slab, row.certifiedOn].filter(Boolean).join(' · ')}
        </span>
      </Link>
    </div>
  );
};

export interface PopulationStats {
  state: StatsBandState;
  isLoading: boolean;
  /* False once loading has ended without usable figures: the card renders nothing. */
  visible: boolean;
  /* /counts monthGraded, or null when the backend sends none: "Graded in …" hides. */
  monthGraded: number | null;
}

/*
  The card's figures. A hook of its own because HomeShowcase needs the same answer to decide
  its layout; RTK Query shares the one request between the two callers.
*/
export const usePopulationStats = (): PopulationStats => {
  const counts = useGetCountsQuery(undefined, { pollingInterval: COUNTS_POLL_MS });
  const games = useGetGamesQuery();

  const isLoading = counts.isLoading || games.isLoading;
  const state = statsBandState({
    counts: counts.data,
    games: games.data,
    isLoading,
    isError: !!counts.error || !!games.error,
  });

  return {
    state,
    isLoading,
    visible: state.status === 'ready' || isLoading,
    monthGraded: selectMonthGraded(counts.data),
  };
};

const PopulationCard = ({ stats }: { stats: PopulationStats }) => {
  const { state, visible, monthGraded } = stats;

  if (!visible) {
    return null;
  }

  const cells = state.status === 'ready' ? state.cells : [];
  const cell = (key: string) => cells.find(c => c.key === key);
  const total = cell('totalGraded')?.value ?? 0;
  const gamesTracked = cell('games');

  return (
    <section
      aria-label="Population Report"
      aria-busy={state.status !== 'ready'}
      className="flex h-full w-full min-w-0 flex-col gap-3.5 rounded-2xl border border-pcg-border bg-pcg-card p-5 text-pcg-text shadow-lg sm:p-6"
      style={{
        backgroundImage:
          'radial-gradient(120% 60% at 100% 0%, rgb(var(--pcg-gold) / 0.14), transparent 60%)',
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-[13px] font-bold uppercase tracking-[0.14em] text-pcg-goldText">
          Population Report
        </p>
        <span className="inline-flex items-center gap-2 rounded-full bg-pcg-danger/10 px-2.5 py-1 text-xs font-bold uppercase tracking-[0.14em] text-pcg-danger">
          <span aria-hidden="true" className="h-2 w-2 animate-pulse rounded-full bg-pcg-danger motion-reduce:animate-none" />
          Live
        </span>
      </div>

      <dl className="min-w-0">
        <dt className={CONTROL_LABEL}>{cell('totalGraded')?.label ?? 'Cards graded'}</dt>
        <dd className="mt-1 text-[clamp(2.5rem,3.6vw,3.25rem)] font-extrabold leading-none tabular-nums tracking-tight text-pcg-goldDisplay">
          {state.status === 'ready' ? <CountUp value={total} /> : BLANK}
        </dd>
        <dd className="mt-2 text-sm text-pcg-textMuted">
          {gamesTracked ? (
            <>
              <span className="font-semibold tabular-nums text-pcg-text">{formatCount(gamesTracked.value)}</span>{' '}
              {gamesTracked.label.toLowerCase()}
              <span aria-hidden="true" className="mx-2 text-pcg-textMuted/60">·</span>
            </>
          ) : null}
          <span className="font-semibold tabular-nums text-pcg-text">{PCG_OFFICE_COUNT}</span> PCG offices worldwide
        </dd>
      </dl>

      <JustCertifiedTicker />

      {/* "Graded in October": its own gold-edged box, as on the storefront. */}
      {state.status === 'ready' && monthGraded !== null ? (
        <dl className="rounded-xl border border-pcg-gold/50 bg-pcg-gold/10 px-4 py-2.5 text-center">
          <dt className={CONTROL_LABEL}>{monthGradedLabel(new Date())}</dt>
          <dd className="mt-0.5 text-2xl font-bold leading-tight tabular-nums text-pcg-goldDisplay">
            <CountUp value={monthGraded} />
          </dd>
        </dl>
      ) : null}

      <dl className="grid grid-cols-2 gap-2.5">
        {GRADE_KEYS.map(key => {
          const tile = cell(key);
          const share = tile ? gradeShare(tile.value, total) : 0;
          return (
            <div key={key} className={`${INNER} min-w-0 px-3.5 pb-3 pt-2.5`}>
              <dt className="flex items-baseline justify-between gap-2">
                <span className={`${CONTROL_LABEL} truncate`}>{tile?.label ?? BLANK}</span>
                {tile ? (
                  <span className="shrink-0 text-xs tabular-nums text-pcg-textMuted">
                    <span className="sr-only">, </span>
                    {formatShare(share)}
                    <span className="sr-only"> of all cards graded</span>
                  </span>
                ) : null}
              </dt>
              <dd className="mt-1 text-xl font-bold tabular-nums">
                {tile ? <CountUp value={tile.value} /> : BLANK}
              </dd>
              <dd aria-hidden="true" className="mt-2 h-[3px] overflow-hidden rounded-full bg-pcg-border">
                <span
                  className="block h-full rounded-full bg-pcg-gold transition-[width] duration-700 ease-out motion-reduce:transition-none"
                  style={{ width: `${share}%` }}
                />
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
};

export default PopulationCard;
